/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {login, loginPageSettings} from '../redux/AuthCRUD'
import {SignInButton} from '../azure/SignInButton'
import { useEffectOnce } from 'react-use'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
  rememberMe: false,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export interface LoginSuccessPayload {
  token?: string
  longTermToken?: string
  refreshToken?: string
  twoFactorEnabled: boolean | null
  twoFactorConnected: boolean
  twoFactorMethod: number | 0
  name?: string
}

interface IProps {
  handleLogin: (payload: LoginSuccessPayload, email: string, rememberMe: boolean) => void
}

//export function Login() {
const Login: React.FC<IProps> = ({ handleLogin }) => {
  const [loading, setLoading] = useState(false)
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const {executeRecaptcha} = useGoogleReCaptcha()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        let captchaToken: string | null = null

        if (captchaEnabled) {
          if (!executeRecaptcha) {
            setStatus('Captcha is not ready. Please refresh and try again.')
            setSubmitting(false)
            setLoading(false)
            return
          }
          captchaToken = await executeRecaptcha('login')
        }

        const {data} = await login(values.email, values.password, captchaToken, values.rememberMe)

        const {
          token,
          longTermToken,
          refreshToken,
          twoFactorEnabled,
          twoFactorConnected,
          twoFactorMethod,
          name,
        } = data

        handleLogin(
          {
            token,
            longTermToken,
            refreshToken,
            twoFactorEnabled,
            twoFactorConnected,
            twoFactorMethod,
            name,
          },
          values.email,
          values.rememberMe
        )
      } catch (error: any) {
        const message = error?.response?.data?.message ?? 'Unable to sign in. Please try again.'
        setStatus(message)
        setSubmitting(false)
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffectOnce(() => {
    loginPageSettings()
      .then(({ data: { captchaEnable } }) => {
        setCaptchaEnabled(captchaEnable);
      });
  })


  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-6'>
          <div className='text-gray-600 fw-semibold mb-4'>Project Cost Management System</div>
          <div className='text-uppercase text-gray-700 fw-bold mb-4'>DCWC Staff Log In</div>
          <SignInButton setStatus={formik.setStatus} captchaEnabled={captchaEnabled}></SignInButton>
          <div className='border-bottom border-gray-300 mt-4 mb-6' style={{width: '100%'}}></div>
        </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-6'>
          <label className='form-label fs-6 fw-bolder text-dark'>Email<span className='text-danger ms-1'>*</span></label>
          <input
            placeholder='Enter your email address'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
          <label className='form-label fw-bolder text-dark fs-6 mb-2'>Password<span className='text-danger ms-1'>*</span></label>
          <input
            placeholder='Enter your password'
            type='password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
            'form-control form-control-lg form-control-solid',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-stack mb-6'>
        <label className='form-check form-check-custom form-check-solid'>
          <input
            type='checkbox'
            className='form-check-input'
            {...formik.getFieldProps('rememberMe')}
            checked={formik.values.rememberMe}
          />
          <span className='form-check-label text-gray-600 ms-2'>Remember Me</span>
        </label>
        <Link to='/auth/forgot-password' className='link-primary fs-6 fw-bolder'>
          Forgot your password?
        </Link>
      </div>

      {/* end::Form group */}

      {/* begin::Action */}
      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg btn-dark w-100 mb-4'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Log In</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-center mt-4'>
        <a
          href='mailto:support@webcert.com'
          className='fs-6 fw-bolder text-gray-600 text-decoration-underline'
        >
          Need Help?
        </a>
      </div>
    </form>
  )
}

export {Login}


